import _ from "lodash";
import {getFieldsByPath} from "../liberality/utils";
import {generateFetchFieldListAction} from "../../../../apps/KpModule/actions/api";
import {setFieldVisibility} from "../../../../apps/KpModule/actions";
import {getDataListList} from "../../../../apps/KpModule/selectors";
import {ObjectId} from "bson";
import {animalEvaluationOptions} from "../staticEntities";
import Errors from "../../../utils/Errors";
import {ageIntervalLabel, findAgeInterval, isFutureBirthDate, parseBirthDate} from "./ageInterval";

// Compatibility targets (types human / animal) used to prefill compatibilityEvaluations.
const COMPATIBILITY_TARGETS_LIST = 'm-C-animal.Animal_compatibilityTargets'

const NOT_EVALUATED = animalEvaluationOptions.find(option => option.id === '3')

// Species with their age intervals, to show the current age interval as soon as a birth date is picked.
const ANIMAL_TYPE_AGE_INTERVALS_LIST = 'm-C-animal.Animal_animalTypeAgeIntervals'

function loadedAnimalTypes(state) {
    try {
        return getDataListList(state, ANIMAL_TYPE_AGE_INTERVALS_LIST)
    } catch (error) {
        return null
    }
}

// Shows the read-only age interval below the birth date, computed like the server-side getter.
// The field can't be declared `hidden` (hidden form fields are dropped from the form), so it
// is hidden here until a birth date is picked.
function refreshAgeInterval({store, module}) {
    const {birthDate, animalType, ageInterval} = getFieldsByPath(module.viewMap.form.fields, ['birthDate', 'animalType', 'ageInterval'])
    const date = birthDate.getValue()
    if (!parseBirthDate(date)) {
        store.dispatch(setFieldVisibility(ageInterval.id, false))
        return ageInterval.setValue(null)
    }

    // list not loaded yet: onOpen refreshes once it is
    const animalTypes = loadedAnimalTypes(store.getState())
    if (!animalTypes?.length) return

    store.dispatch(setFieldVisibility(ageInterval.id, true))
    const animalTypeId = animalType.getValue()?.id
    if (!animalTypeId) return ageInterval.setValue("Choisir l'espèce pour calculer la tranche d'âge")

    const ageIntervals = animalTypes.find(type => type.id === animalTypeId)?.ageIntervals
    ageInterval.setValue(
        ageIntervalLabel(findAgeInterval(date, ageIntervals)) || "Aucune tranche d'âge de l'espèce ne correspond à cet âge"
    )
}

const isHumanTarget = target => (target.type?.id || target.type) === 'human' ? 1 : 0

// One row per compatibility target (Compatibility of type human/animal), keeping
// the evaluation already entered and defaulting new targets to "Non évaluée".
// Humans first, then by name, so the rows always appear in the same order.
function buildCompatibilityEvaluationRows(targets, rows) {
    const rowByTargetId = new Map(rows.filter(row => row.compatibility?.id).map(row => [row.compatibility.id, row]))
    return [...targets]
        .sort((a, b) => isHumanTarget(b) - isHumanTarget(a)
            || String(a.name || '').localeCompare(String(b.name || ''), 'fr', {sensitivity: 'base'}))
        .map(target => rowByTargetId.get(target.id) || {
            id: new ObjectId().toString(),
            compatibility: {id: target.id, name: target.name},
            evaluation: NOT_EVALUATED
        })
}

export const animalCompatibilityEvaluationEntity = {
    name: 'AnimalCompatibilityEvaluation',
    fields: [
        {path: 'compatibility', type: 'Compatibility'},
        // Emptiness is checked by Animal.validateSave, with a message naming the target.
        {path: 'evaluation', type: 'AnimalEvaluationOption', nullable: true}
    ]
}

export const entity = {
    name: 'Animal',
    fields: [
        {path: 'organization', type: 'Organization'},
        {
            path: 'organizationStatus',
            fieldPath: ['organization.status'],
            $f: (animal, context, callback) => {
                return callback(null, animal?.organization?.status?.name)
            }
        },
        {path: 'establishment', type: 'Shelter'},
        {path: 'animalType', type: 'AnimalType'},
        {path: 'race', type: 'Race'},
        {path: 'size', type: 'AnimalSize'},
        {path: 'sex', type: 'AnimalSex'},
        {path: 'birthDate', type: 'month'},
        {
            path: 'ageInterval',
            fieldPath: ['birthDate', 'animalType.ageIntervals.minAge', 'animalType.ageIntervals.maxAge'],
            $f: (animal, context, callback) => {
                return callback(null, ageIntervalLabel(findAgeInterval(animal.birthDate, animal.animalType?.ageIntervals)))
            }
        },
        {path: 'currentHome', type: 'AnimalHome'},
        {path: 'compatibilityEvaluations', type: 'AnimalCompatibilityEvaluation', link: {type: 'OTM', deleteType: 'cascade', onParent: true, onChild: true}},
        {
            path: 'compatibilitySummary',
            fieldPath: ['compatibilityEvaluations.compatibility.name', 'compatibilityEvaluations.evaluation.id'],
            $f: (animal, context, callback) => {
                const summary = (animal.compatibilityEvaluations || [])
                    .filter(row => row?.compatibility)
                    .map(row => {
                        const evaluation = animalEvaluationOptions.find(option => option.id === row.evaluation?.id) || NOT_EVALUATED
                        return `${row.compatibility.name} : ${evaluation.name}`
                    })
                    .join(' · ')
                return callback(null, summary)
            }
        },
        {path: 'characters', type: 'Character', link: 'MTM', nullable: true},
        {path: 'toleranceToSolitudeEvaluation', type: 'AnimalEvaluationOption'},
        {path: 'handicaps', type: 'Handicap', link: 'MTM', nullable: true},
        {path: 'suitablePlaces', type: 'Compatibility', link: 'MTM'},
        {path: 'hasAdoptionFee', type: 'YesNo'},
        {
            path: 'hasAdoptionFeeBoolean',
            type: 'boolean',
            fieldPath: ['hasAdoptionFee.id'],
            $f: (animal, context, callback) => {
                return callback(null, animal?.hasAdoptionFee?.id === 'yes')
            }
        },
        {path: 'adoptionFee', type: 'decimal'},
        {path: 'adoptionStatus', type: 'AdoptionStatus', nullable: true},
        {path: 'name'},
        {path: 'description'},
        {path: 'history'},
        {path: 'images', type: 'Image', link: {type: 'OTM', deleteType: 'cascade'}},
    ],
    filters: [
        {
            name: 'byAdoptionStatus',
            isDefault: false,
            path: 'adoptionStatus',
            tKey: 'status',
            placeholder: 'Sélectionner un statut',
            object: 'AdoptionStatus',
            display: 'name',
            type: 'tags',
            default: [{id: 'draft'}, {id: 'toBePresented'}],
            client: true,
            query: function (context) {
                const statusIds = (_.get(context, 'data.adoptionStatus') || []).map(status => status.id)
                if (!statusIds.length) return {}
                return {adoptionStatus: {$in: statusIds}}
            }
        }
    ],
    validateSave: function(newObject, oldObject, context, callback) {
        if (isFutureBirthDate(newObject.birthDate)) {
            return callback(new Errors.ValidationError("La date de naissance ne peut pas être dans le futur"))
        }

        const missingRows = (newObject.compatibilityEvaluations || [])
            .filter(row => row?.compatibility && !row.evaluation?.id)
        if (!missingRows.length) return callback()

        const targetNames = missingRows.map(row => row.compatibility.name).filter(Boolean)
        return callback(new Errors.ValidationError(
            targetNames.length === missingRows.length
                ? `Veuillez renseigner l'évaluation de compatibilité pour : ${targetNames.join(', ')}`
                : "Veuillez renseigner toutes les évaluations de compatibilité"
        ))
    }
}

export const module_ = {
    object: 'Animal',
    accesses: [
        {
            id: COMPATIBILITY_TARGETS_LIST,
            entity: 'Compatibility',
            fieldPath: ['id', 'name', 'type'],
            filters: ['humanOrAnimal']
        },
        {
            id: ANIMAL_TYPE_AGE_INTERVALS_LIST,
            entity: 'AnimalType',
            fieldPath: ['id', 'ageIntervals.minAge', 'ageIntervals.maxAge']
        }
    ],
    tKey: 'mTitle_animalsToAdopt',
    defaultSortBy: 'name',
    defaultSortDirection: 'ASC',
    category: {
        path: 'mTitle_animals',
        icon: 'animal'
    },
    viewMap: {
        dt: [
            'name',
            {path: 'organizationStatus', tKey: 'associationStatus', initiallyNotVisible: true},
            {path: 'organization', tKey: 'association', initiallyNotVisible: true},
            'establishment',
            'animalType',
            'race',
            {path: 'size', initiallyNotVisible: true},
            {path: 'sex', initiallyNotVisible: true},
            {path: 'birthDate', initiallyNotVisible: true},
            {path: 'ageInterval', tKey: 'ageInterval'},
            {path: 'currentHome', initiallyNotVisible: true},
            {path: 'toleranceToSolitudeEvaluation', tKey: 'toleranceToSolitude', initiallyNotVisible: true},
            {path: 'compatibilitySummary', tKey: 'compatibility(s)'},
            {path: 'characters', initiallyNotVisible: true},
            {path: 'handicaps', tKey: 'handicap(s)', initiallyNotVisible: true},
            {path: 'suitablePlaces', initiallyNotVisible: true},
            {path: 'hasAdoptionFeeBoolean', tKey: 'adoptionFee', initiallyNotVisible: true},
            {path: 'adoptionStatus', tKey: 'status'}
        ],
        form: {
            fields: [
                {
                    path: 'organization',
                    widthClassName: "col-xs-12 col-sm-6",
                    filters: ['isMemberAssociation'],
                    subscriptions: {
                        onChange: (newValue, oldValue, {store, module}) => {
                            const fields = getFieldsByPath(module.viewMap.form.fields, ['establishment'])
                            const changedValue = !!oldValue?.id && !!newValue?.id && oldValue.id !== newValue.id
                            if (changedValue) fields?.establishment?.setValue(null)
                            store.dispatch(
                                generateFetchFieldListAction(
                                    "m-C-animal.Animal_establishment",
                                    store.getState,
                                    {
                                        data: {organizationId: newValue?.id}
                                    }
                                )
                            )
                        }
                    }
                },
                {
                    path: 'establishment',
                    widthClassName: "col-xs-12 col-sm-6",
                    required: true,
                    filters: ['byOrganization']
                },
                {
                    path: 'animalType',
                    widthClassName: "col-xs-12 col-sm-6",
                    subscriptions: {
                        onChange: (newValue, oldValue, {store, module}) => {
                            refreshAgeInterval({store, module})
                            store.dispatch(
                                generateFetchFieldListAction(
                                    "m-C-animal.Animal_race",
                                    store.getState,
                                    {
                                        data: {animalType: newValue}
                                    }
                                )
                            )
                        }
                    }
                },
                {
                    path: 'race',
                    widthClassName: "col-xs-12 col-sm-6",
                    filters: ['byAnimalType']
                },
                {path: 'size', widthClassName: "col-xs-12 col-sm-6",},
                {path: 'sex', widthClassName: "col-xs-12 col-sm-6",},
                {
                    path: 'birthDate',
                    type: 'monthPicker',
                    required: true,
                    widthClassName: "col-xs-12 col-sm-6",
                    subscriptions: {
                        onChange: (newValue, oldValue, {store, module}) => refreshAgeInterval({store, module})
                    }
                },
                {path: 'ageInterval', tKey: 'ageInterval', type: 'readOnly', widthClassName: "col-xs-12 col-sm-6"},
                {path: 'currentHome', widthClassName: "col-xs-12 col-sm-6",},
                {path: 'toleranceToSolitudeEvaluation', tKey: 'toleranceToSolitude', widthClassName: "col-xs-12 col-sm-6",},
                {
                    path: 'compatibilityEvaluations',
                    tKey: 'compatibility',
                    type: 'dtObjects',
                    editable: true,
                    fields: [
                        {path: 'compatibility', tKey: 'compatibilityTarget', type: 'object'},
                        {path: 'evaluation', tKey: 'evaluation', type: 'dropdownObject', customOptions: animalEvaluationOptions, display: 'name', width: 200}
                    ]
                },
                {path: 'characters'},
                {path: 'handicaps', tKey: 'handicap(s)'},
                {path: 'suitablePlaces', filters: ['place']},
                {
                    path: 'hasAdoptionFee',
                    tKey: 'adoptionFee',
                    required: true,
                    default: {id: 'no', name: 'Non'},
                    subscriptions: {
                        onChange: (newValue, oldValue, {store, module}) => {
                            const fields = getFieldsByPath(module.viewMap.form.fields, ['adoptionFee'])
                            const isYes = newValue?.id === 'yes'
                            store.dispatch(setFieldVisibility(fields?.adoptionFee?.id, isYes))
                            if (!isYes) fields?.adoptionFee?.setValue(0)
                        }
                    }
                },
                {path: 'adoptionFee', tKey: 'amount(€)', type: 'number', numberOfDecimals: 2, wholePositiveNumber: true, required: true, default: 0},
                {path: 'adoptionStatus', tKey: 'adoptionStatus', required: true, clearable: false, default: {id: 'draft', name: 'Brouillon'}},
                {path: 'name', tKey: 'animalName', required: true},
                {path: 'description', type: 'textarea'},
                {path: 'history', type: 'textarea'},
                {path: 'images', tKey: 'photos', placeholder: 'addPhoto(s)', type: 'image', fieldPath: ['id', 'name', 'user.id', 'user.name', 'date'], required: true},
            ],
            dataLists: [COMPATIBILITY_TARGETS_LIST, ANIMAL_TYPE_AGE_INTERVALS_LIST],
            onOpen: ({store, module}) => {
                const {ageInterval} = getFieldsByPath(module.viewMap.form.fields, ['ageInterval'])
                store.dispatch(setFieldVisibility(ageInterval.id, false))
                store.dispatch(generateFetchFieldListAction(ANIMAL_TYPE_AGE_INTERVALS_LIST, store.getState))
                    .then(() => refreshAgeInterval({store, module}))
                    .catch(error => console.error('[Animal] age intervals fetch failed', error))
                try {
                    const {compatibilityEvaluations} = getFieldsByPath(module.viewMap.form.fields, ['compatibilityEvaluations'])
                    store.dispatch(generateFetchFieldListAction(COMPATIBILITY_TARGETS_LIST, store.getState))
                        .then(response => {
                            if (response?.error) return console.error('[Animal] compatibility targets fetch failed', response)
                            compatibilityEvaluations.setValue(
                                buildCompatibilityEvaluationRows(response?.data || [], compatibilityEvaluations.getValue() || [])
                            )
                        })
                        .catch(error => console.error('[Animal] compatibility prefill failed', error))
                } catch (error) {
                    console.error('[Animal] compatibility prefill failed', error)
                }
            }
        }
    },
    filters: ['byAdoptionStatus']
}
